import { membershipQueries } from "@app/config/queryFactory";
import { ERROR_CODES } from "@base/lib/error";
import { isAxiosApiError } from "@base/lib/utils";
import { postApplyMembershipPayment } from "@features/payment/api/request";
import type { PaymentMessage } from "@features/payment/model/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const serviceUrl = typeof window !== "undefined" ? window.location.origin : "";

const ALLOWED_ORIGIN = window.location.origin;

const useApplyMembershipPayment = () => {
  const paymentWindowRef = useRef<Window | null>(null);

  const { t } = useTranslation();

  const [paymentId, setPaymentId] = useState<string | null>(null);

  const paymentMutation = useMutation({
    mutationFn: postApplyMembershipPayment,
    onSuccess: (data: { hosted_page: string; payment_id: string }) => {
      const redirectUrl = encodeURIComponent(data.hosted_page);

      const newWindow = window.open(`${serviceUrl}/payment/callback?redirectUrl=${redirectUrl}`);

      if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
        toast.error(t("apply.payment.failedToOpenPaymentWindow"));
        return;
      }

      paymentWindowRef.current = newWindow;
      setPaymentId(data.payment_id);
    },
    onError: (error) => {
      if (isAxiosApiError(error) && error.response.data.code === ERROR_CODES.INVALID_REQUEST_DATA) {
        toast.error(t("apply.payment.invalidRequestData"));
        return;
      }
      toast.error(t("apply.payment.failedToProcessPayment"));
    },
  });

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      // (A) Origin 검증
      if (event.origin !== ALLOWED_ORIGIN) return;

      // (B) Source 검증: 우리가 연 팝업이 보낸 것인지 확인
      //     팝업이 차단/재생성되면 달라질 수 있으니 상황에 따라 완화 가능
      if (!paymentWindowRef.current || event.source !== paymentWindowRef.current) return;

      const data = event.data as PaymentMessage;
      if (!data || typeof data !== "object" || !("type" in data)) return;

      // (C) 메시지 타입별 처리
      if (data.type === "PAYMENT_SUCCEEDED" && data.paymentId === paymentId) {
        navigate({ to: "/apply/success" });

        queryClient.invalidateQueries({ queryKey: ["userInfo"], refetchType: "all" });
        queryClient.invalidateQueries({ queryKey: membershipQueries.all(), refetchType: "all" });
      }

      if (data.type === "PAYMENT_FAILED") {
        toast.error(t("apply.payment.failedToProcessPayment"));
      }

      paymentWindowRef.current = null;

      // (D) 정리
      // try {
      //   paymentWindowRef.current?.close();
      // } catch {}
      // paymentWindowRef.current = null;
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate, paymentId, t, queryClient]);

  return paymentMutation;
};

export default useApplyMembershipPayment;
